// Fixed values of the backoffice — every value that is still written into the code, in ONE place
// (root CLAUDE.md, „Keine Werte im Code“ → „Konstantendatei je Projekt — erst einsammeln, dann
// entscheiden“). Client and server both import from here.
//
// Every entry carries a STATUS:
//   fest (Betreiber, TT.MM.JJJJ) — decided: stays a constant.
//   offen                        — not decided yet. Collected as it was; the operator decides per
//                                  entry. Becomes it „änderbar“, it moves into settings, templates
//                                  or the database and leaves this file.
// Collecting changes no behaviour: every value is exactly what stood at its old place.
//
// NOT here, on purpose: units and formats (mm→pt, ms per day, ISO codes, schema versions, field
// lengths and codes of a standard such as SEPA/ZUGFeRD), identifiers (status keys, folder names,
// storage keys), layout geometry (coordinates, font sizes, widths), texts (shared/texts/de.ts) and
// test helpers. The defaults of settings fields ARE here (SETTINGS_DEFAULTS): the field is
// changeable, but its start value stood three times in the code.
// New values in the code come here right away, as `offen`.

// ── Firma ──────────────────────────────────────────────────────────────────────────────────

// The legal name of the company, the one code constant for it (root CLAUDE.md). It names the
// issuer only where nothing else does (the sales/VAT report); invoices and certificates take the
// issuer from the brand's fields (services/issuer.ts). · Status: fest (Betreiber, 26.09.2026)
export const LEGAL_ENTITY = 'Detlef and Jacielma Services Limited'

// The brands in the client's pickers and lists, in this order; their names are CLIENT_BRAND_NAMES
// in shared/texts. Mirrors the server's brand set (models/brands.ts). · Status: fest (Betreiber, 19.10.2026)
export const CLIENT_BRAND_KEYS = ['d', 'C', 'V'] as const
// The brand a new invoice / abo / mail preview starts with in the client. · Status: fest (Betreiber, 19.10.2026)
export const DEFAULT_FORM_BRAND = 'd'
// The brand the author settlement belongs to (vitalpilze.de). · Status: fest (Betreiber, 19.10.2026)
export const AUTHOR_BRAND = 'V'

// Company seat for the VAT rule when nothing is stored (OSS turnover, author tax case, VAT
// config, the VAT page). Default for the setting vatBaseCountry (VAT page) and the env
// COMPANY_COUNTRY. · Status: fest (Betreiber, 19.10.2026)
export const DEFAULT_BASE_COUNTRY = 'CY'
// Home country of the certificate mail: an address in it is printed without the country line.
// · Status: fest (Betreiber, 19.10.2026)
export const HOME_COUNTRY = 'DE'

// The real company address in the bundled deja template, replaced by a placeholder address
// (SERVICES_PDF_TEMPLATE_RENDER_TEXTS.newTemplatePlaceholderAddress) when a new template is created
// from it (pdfTemplate/render.ts). It must match the template word for word.
// · Status: fest (Betreiber, 19.10.2026)
export const SEED_TEMPLATE_ADDRESS_LINE = 'deja.ltd, Agiou Panaretou 26 M01, 8036 Paphos, Cyprus'

// Sender when no SMTP sender is configured. Default for the setting smtpFrom (env SMTP_FROM) and the
// brand's own sender. · Status: fest (Betreiber, 19.10.2026)
export const DEFAULT_MAIL_FROM = 'no-reply@deja.ltd'

// ── Steuern und Beträge ────────────────────────────────────────────────────────────────────

// EU standard VAT rates by country (services/vatRates.ts). Stand: 2026-07 — must be verified
// against an authoritative source / the tax advisor before OSS go-live. Default for the setting
// vatRateOverrides (VAT page), which overrides single countries. · Status: fest (Betreiber, 19.10.2026)
export const EU_STANDARD_VAT_RATES: Record<string, number> = {
    AT: 20, BE: 21, BG: 20, CY: 19, CZ: 21, DE: 19, DK: 25, EE: 22, ES: 21,
    FI: 25.5, FR: 20, GR: 24, HR: 25, HU: 27, IE: 23, IT: 22, LT: 21, LU: 17,
    LV: 21, MT: 18, NL: 21, PL: 23, PT: 23, RO: 19, SE: 25, SI: 22, SK: 23,
}
// Fallback VAT rate (CY) when a country is not in the rate table. · Status: fest (Betreiber, 19.10.2026)
export const STANDARD_VAT_PERCENT = 19
// VAT rate a new invoice line starts with in the client. · Status: fest (Betreiber, 19.10.2026)
export const DEFAULT_LINE_TAX_RATE = 19
// EU-wide annual threshold (€, net) for cross-border B2C distance sales of TBE services; below it
// the seat rate may be charged, above it the destination rate (OSS). · Status: fest (Betreiber, 19.10.2026)
export const OSS_THRESHOLD_EUR = 10000
// Step of the OSS-threshold input on the VAT page. · Status: fest (Betreiber, 19.10.2026)
export const OSS_THRESHOLD_STEP = 100
// An amount counts as non-zero above this (rounding noise of cents). · Status: fest (Betreiber, 19.10.2026)
export const MONEY_EPSILON = 0.005
// Mollie refund statuses that moved no money; they do not count as refunded (MollieProvider
// plausibleRefunded). Mollie's own API vocabulary. · Status: offen
export const MOLLIE_REFUND_NO_MONEY: readonly string[] = ['failed', 'canceled']

// ── Abos und Zeitpläne ─────────────────────────────────────────────────────────────────────

// At most this many open periods per abo in one billing run (services/aboBilling.ts openPeriods).
// Protects against a typo in the next billing date (2013 instead of 2026): more than that is
// refused, the error stands at the abo and nothing is billed. Moved out of the settings.
// · Status: fest (Betreiber, 19.10.2026: „ok, bleibt bei 12. Das wäre ein Fall für eine Konstante
//   im Code (nicht .env).“)
export const ABO_MAX_BACKLOG = 12
// Latest run day of a month an abo may name — so it falls in EVERY month. · Status: fest (Betreiber, 19.10.2026)
export const ABO_RUN_DAY_MAX = 28
// Ninox abo takeover (scripts/import-abos-ninox.ts): Ninox billed up to and including September, the
// backoffice bills from this day on. A row due BEFORE it is refused — the first run would bill it
// again. · Status: offen (Betreiber, 31.10.2026, 20a/22a)
export const NINOX_ABO_TAKEOVER_DAY = '2026-10-01'
// Mode of the imported Ninox abos: the first run makes a draft the operator finalizes himself; he
// switches them to 'auto' afterwards. · Status: fest (Betreiber, 31.10.2026, 24a)
export const NINOX_ABO_IMPORT_MODE = 'draft'
// Latest hour of the daily abo run (0–23, a clock hour). · Status: offen
export const ABO_RUN_HOUR_MAX = 23
// Range of the monthly payment auto-import day, for the same reason. · Status: fest (Betreiber, 19.10.2026)
export const PAYMENT_AUTO_IMPORT_DAY_MAX = 28
// Range of the mailbox poll interval (hours). · Status: fest (Betreiber, 19.10.2026)
export const INGEST_POLL_HOURS_MAX = 168
// Range of the log retention (days). · Status: fest (Betreiber, 19.10.2026)
export const LOG_RETENTION_DAYS_MAX = 3650
// A missed payment auto-import is caught up this long after the start. · Status: fest (Betreiber, 19.10.2026)
export const PAYMENT_IMPORT_STARTUP_DELAY_MS = 60_000
// Interval of the vitalpilze delivery queue sweep (minutes). · Status: fest (Betreiber, 19.10.2026)
export const VP_DELIVERY_SWEEP_MINUTES = 15
// Interval of the VIES recheck of authors (hours). · Status: fest (Betreiber, 19.10.2026)
export const VIES_RECHECK_INTERVAL_HOURS = 6
// How far back the search for the next open month goes (months). · Status: fest (Betreiber, 19.10.2026)
export const OPEN_MONTH_SEARCH_MAX_MONTHS = 36
// Years a period (year/month in a route) may name. · Status: fest (Betreiber, 19.10.2026)
export const PERIOD_YEAR_MIN = 2020
export const PERIOD_YEAR_MAX = 2099

// ── Zustellung an vitalpilze.de ────────────────────────────────────────────────────────────

// Immediate attempts of a webhook before the queue takes over, and the base of their backoff
// (500 ms, 1 s). · Status: fest (Betreiber, 19.10.2026)
export const VP_WEBHOOK_MAX_ATTEMPTS = 3
export const VP_WEBHOOK_RETRY_BASE_MS = 500
// The queue's retry ladder: narrow at first, because most outages last minutes, then wide, so a
// longer one does not log every hour. · Status: fest (Betreiber, 19.10.2026)
export const VP_QUEUE_BACKOFF_MS = [
    1 * 60_000,        // 1 min
    5 * 60_000,        // 5 min
    15 * 60_000,       // 15 min
    60 * 60_000,       // 1 h
    3 * 60 * 60_000,   // 3 h
    6 * 60 * 60_000,   // 6 h
    12 * 60 * 60_000,  // 12 h
]
export const VP_QUEUE_BACKOFF_MAX_MS = 12 * 60 * 60_000
// ⛔ SEVEN DAYS, then the queue gives up — by AGE, not by attempts: that is what a person can judge
// („since Monday“). WordPress unreachable for more than a week is an incident, not a delivery
// problem. · Status: fest (Betreiber, 19.10.2026)
export const VP_QUEUE_GIVE_UP_AFTER_MS = 7 * 24 * 60 * 60_000
// Timeout of one delivery to vitalpilze. · Status: fest (Betreiber, 19.10.2026)
export const VP_TRANSPORT_TIMEOUT_MS = 15000
// Rows per status in the delivery overview (Webhooks). · Status: fest (Betreiber, 19.10.2026)
export const VP_DELIVERY_LIST_LIMIT = 50

// ── Fremde Dienste ─────────────────────────────────────────────────────────────────────────

// Timeout of an outgoing webhook. · Status: fest (Betreiber, 19.10.2026)
export const WEBHOOK_TIMEOUT_MS = 5000
// A provider's error body is cut to this many characters in our message. · Status: fest (Betreiber, 19.10.2026)
export const ERROR_BODY_MAX_CHARS = 300
// VIES (EU VAT id check): endpoint, timeout, how long a result is cached. · Status: fest (Betreiber, 19.10.2026)
export const VIES_ENDPOINT = 'https://ec.europa.eu/taxation_customs/vies/rest-api/ms/'
export const VIES_TIMEOUT_MS = 10_000
export const VIES_CACHE_TTL_MS = 30 * 60_000
// VIES recheck of authors: VIES is an authority's service, so a run asks at most this many, with a
// gap between two requests, and stops after this many failures in a row. · Status: fest (Betreiber, 19.10.2026)
export const VIES_MAX_PER_RUN = 25
export const VIES_REQUEST_GAP_MS = 1000
export const VIES_ABORT_AFTER_FAILURES = 3
// Telegram (certificate notices). · Status: fest (Betreiber, 19.10.2026)
export const TELEGRAM_API_URL = 'https://api.telegram.org'
export const TELEGRAM_TIMEOUT_MS = 15_000
// onlinebrief24 (letter print): endpoint, largest PDF it takes, timeout; letters that failed are
// tried this often (the sweep runs daily → five days). · Status: fest (Betreiber, 19.10.2026)
export const ONLINEBRIEF24_API_URL = 'https://api.onlinebrief24.de/v1'
export const ONLINEBRIEF24_MAX_PDF_BYTES = 50 * 1024 * 1024
export const ONLINEBRIEF24_TIMEOUT_MS = 30_000
export const PRINT_MAX_ATTEMPTS = 5
// PayPal API (live / sandbox) and the page size of the transaction search. · Status: fest (Betreiber, 19.10.2026)
export const PAYPAL_API_URL_LIVE = 'https://api-m.paypal.com'
export const PAYPAL_API_URL_SANDBOX = 'https://api-m.sandbox.paypal.com'
export const PAYPAL_PAGE_SIZE = 500
// Mollie balance report endpoint. · Status: fest (Betreiber, 19.10.2026)
export const MOLLIE_BALANCE_REPORT_URL = 'https://api.mollie.com/v2/balances/primary/report'
// Mollie's own fee invoices (Betreiber, 03.11.2026, 51a): Mollie mails „Rechnung MOL-NL-R2026.…“ with a
// dashboard link only; the PDF comes from the Invoices API (scope invoices.read). The mail is recognised
// by sender domain + a reference in the subject (named groups ref, year). · Status: offen
export const MOLLIE_INVOICES_URL = 'https://api.mollie.com/v2/invoices'
export const MOLLIE_INVOICES_PAGE_SIZE = 250
export const MOLLIE_INVOICE_MAIL_DOMAIN = 'mollie.com'
export const MOLLIE_INVOICE_SUBJECT_PATTERN = '\\b(?<ref>[A-Z]{3}-[A-Z]{2}-R?(?<year>\\d{4})\\.\\d+)\\b'
// Mollie's booking type for the rounding of withheld fees (balance report, a subtotal under
// fee-prepayments: prepaymentPartType). Mollie withholds fees rounded UP; its invoice rounds per line
// and settles the difference with the next month's invoice (53b, Betreiber 03.11.2026). · Status: offen
export const MOLLIE_FEE_ROUNDING_TYPES: readonly string[] = ['fee-rounding-compensation']
// Kept copies of an invoice PDF before an admin changed a position text (Betreiber 03.11.2026, 64b): a folder
// under storageDir, deliberately OUTSIDE accounting/ so the monthly export never carries two PDFs under one
// number; maximum length of the new text and of the required reason. · Status: offen
export const INVOICE_REVISIONS_DIR = 'invoice-revisions'
export const INVOICE_LINE_TEXT_MAX = 2000
export const INVOICE_TEXT_CHANGE_REASON_MAX = 500
// Mollie balance-report categories (grouping transaction-categories) → our statement field. Mollie's
// own API vocabulary; anything not listed lands in `other`. · Status: offen
// Stripe balance transactions: reporting_category → our statement field (Stripe's own vocabulary,
// https://docs.stripe.com/reports/reporting-categories). Anything not listed lands in `other`.
// Fees are NOT a category here: every booking carries its own `fee`. · Status: offen
export const STRIPE_BALANCE_CATEGORIES: Record<string, 'payments' | 'refunds' | 'chargebacks' | 'fees' | 'payouts' | 'topups'> = {
    charge: 'payments',
    refund: 'refunds',
    dispute: 'chargebacks',
    dispute_reversal: 'chargebacks',
    payout: 'payouts',
    payout_reversal: 'payouts',
    topup: 'topups',
    topup_reversal: 'topups',
    fee: 'fees',
}
// PayPal transaction_event_code → our statement field: the exact code first, then its group (first
// three characters). PayPal's own vocabulary (T00 payments — negative = sent from the balance,
// T01 fees, T03 bank deposits = top-ups, T04 withdrawals = payouts, T11 refunds, T12 adjustments;
// T1106 is a reversal PayPal initiated, i.e. a chargeback). Anything else → `other`. · Status: offen
export const PAYPAL_EVENT_CATEGORIES: Record<string, 'payments' | 'refunds' | 'chargebacks' | 'fees' | 'payouts' | 'topups'> = {
    T00: 'payments',
    T01: 'fees',
    T03: 'topups',
    T04: 'payouts',
    T11: 'refunds',
    T12: 'chargebacks',
    T1106: 'chargebacks',
}
export const MOLLIE_BALANCE_CATEGORIES: Record<string, 'payments' | 'refunds' | 'chargebacks' | 'fees' | 'payouts' | 'topups'> = {
    payments: 'payments',
    refunds: 'refunds',
    chargebacks: 'chargebacks',
    'fee-prepayments': 'fees',
    transfers: 'payouts',
    topups: 'topups',
}
// Page size of Stripe list calls (charges, balance transactions, tax rates, webhook endpoints).
// · Status: fest (Betreiber, 19.10.2026)
export const STRIPE_LIST_PAGE_SIZE = 100
// A Stripe report run is waited for at most this long, asking at this interval. · Status: fest (Betreiber, 19.10.2026)
export const STRIPE_REPORT_WAIT_MAX_MS = 120_000
export const STRIPE_REPORT_POLL_INTERVAL_MS = 2000
// PDF download from a receipt mail: timeout and the User-Agent we announce. · Status: fest (Betreiber, 19.10.2026)
export const INGEST_DOWNLOAD_TIMEOUT_MS = 15000
export const INGEST_USER_AGENT = 'Mozilla/5.0 (backoffice-ingest)'
// Local LLM (receipt reading): longest text handed over, temperature. · Status: fest (Betreiber, 19.10.2026)
export const LLM_INPUT_MAX_CHARS = 8000
export const LLM_TEMPERATURE = 0

// ── Listen und Abfragen ────────────────────────────────────────────────────────────────────

// Server list routes: page size when none is asked for, and the largest one granted. · Status: fest (Betreiber, 19.10.2026)
export const LIST_PAGE_SIZE_DEFAULT = 50
export const LIST_PAGE_SIZE_MAX = 200
// Author portal API (vitalpilze reads it): page size and its ceiling — the other side relies on
// them. · Status: fest (Betreiber, 19.10.2026)
export const AUTHOR_PORTAL_DEFAULT_LIMIT = 200
export const AUTHOR_PORTAL_MAX_LIMIT = 1000
// Salutation codes of an author — stored and sent as code (registry contract, INTEGRATION.md D),
// rendered from the texts (AUTHOR_SALUTATION_LABELS). · Status: fest (Betreiber, 25.10.2026: „Anrede Herr/Frau")
export const AUTHOR_SALUTATIONS = ['mr', 'ms'] as const
export type AuthorSalutation = typeof AUTHOR_SALUTATIONS[number]
// Longest title of an author (free text such as „Dr.", „HP"); longer is rejected, not cut.
// Part of the registry contract. · Status: offen
export const AUTHOR_TITLE_MAX_LENGTH = 40
// Sales of one author fetched at most in one request. · Status: fest (Betreiber, 19.10.2026)
export const AUTHOR_SALES_QUERY_LIMIT = 500
// Entries of the receipt inbox shown at most. · Status: fest (Betreiber, 19.10.2026)
export const INGEST_LIST_LIMIT = 100
// To-dos shown at most. · Status: fest (Betreiber, 19.10.2026)
export const TODO_LIST_LIMIT = 500
// Certificates in „zuletzt versandt“. · Status: fest (Betreiber, 19.10.2026)
export const CERT_RECENTLY_SENT_LIMIT = 25
// Most rows a bulk move to the trash takes in one request. · Status: fest (Betreiber, 19.10.2026)
export const BULK_TRASH_MAX = 5000

// Client lists: rows per page to choose from, the start value of a table, of the server-list hook,
// of the customer picker, of the log viewer and of the dashboard lists. · Status: fest (Betreiber, 19.10.2026)
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
export const DEFAULT_TABLE_PAGE_SIZE = 20
export const DEFAULT_LIST_PAGE_SIZE = 50
export const CUSTOMER_PICKER_PAGE_SIZE = 20
export const LOG_VIEW_PAGE_SIZE = 50
export const DASHBOARD_LIST_SIZE = 5
// Lookups that load a whole small collection at once (roles, catalog, authors). · Status: fest (Betreiber, 19.10.2026)
export const LOOKUP_FETCH_LIMIT = 200

// ── Uploads und Ablage ─────────────────────────────────────────────────────────────────────

// Largest file per upload: receipts, template images, mail-frame logos. · Status: fest (Betreiber, 19.10.2026)
export const DOCUMENT_UPLOAD_MAX_BYTES = 50 * 1024 * 1024
export const TEMPLATE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024
export const MAIL_LOGO_UPLOAD_MAX_BYTES = 2 * 1024 * 1024
// Allowed width of the logo in the mail frame (px). · Status: fest (Betreiber, 19.10.2026)
export const MAIL_LOGO_WIDTH_MIN = 40
export const MAIL_LOGO_WIDTH_MAX = 600
// Log files: size of one file and how many rotated files are kept. · Status: fest (Betreiber, 19.10.2026)
export const LOG_FILE_MAX_BYTES = 5 * 1024 * 1024
export const LOG_FILE_MAX_FILES = 5
// Action links in mails and pending checkouts are deleted after this (Mongo TTL index, seconds).
// · Status: fest (Betreiber, 19.10.2026)
export const ACTION_REQUEST_TTL_SECONDS = 60 * 60 * 24 * 30
export const PENDING_CHECKOUT_TTL_SECONDS = 60 * 60 * 24 * 30
// Compression level of the monthly export ZIP (zlib 0–9). · Status: fest (Betreiber, 19.10.2026)
export const EXPORT_ZIP_COMPRESSION_LEVEL = 9

// ── Sitzung und Sicherheit ─────────────────────────────────────────────────────────────────

// How long a login lasts. · Status: fest (Betreiber, 19.10.2026)
export const SESSION_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30
// bcrypt cost factor. · Status: fest (Betreiber, 19.10.2026)
export const BCRYPT_SALT_ROUNDS = 10
// Password rule: shortest length and fewest character classes (server and the strength meter in
// the client). · Status: fest (Betreiber, 19.10.2026)
export const MIN_PASSWORD_LENGTH = 8
export const MIN_PASSWORD_CHAR_CLASSES = 2
// Strength meter: length that earns the extra point, and the score accepted. · Status: fest (Betreiber, 19.10.2026)
export const STRONG_PASSWORD_LENGTH = 12
export const MIN_PASSWORD_SCORE = 2
// Characters of a secret shown behind the dots (••••abcd), and IBAN digits shown. · Status: fest (Betreiber, 19.10.2026)
export const SECRET_MASK_VISIBLE_CHARS = 4
export const IBAN_MASK_VISIBLE_DIGITS = 4

// ── Vorgaben der Einstellungen ─────────────────────────────────────────────────────────────

// What a field in Einstellungen holds until the operator sets it — ONE place for the schema
// default (models/settings.ts), the fallback in services/settings.ts and the form's start value
// (pages/Settings.tsx). Empty texts and secrets are no values and stay '' where they are; the
// text defaults (mail subjects and bodies, export lines) are in shared/texts/de.ts
// (SETTINGS_TEXT_DEFAULTS). · Status: offen (each entry)
export const SETTINGS_DEFAULTS = {
    stripeTestmode: true,
    paypalTestmode: true,
    smtpPort: 587,
    smtpSecure: false,
    testMailMode: false,
    imapPort: 993,
    imapSecure: true,
    imapMailbox: 'INBOX',
    imapTrashMailbox: 'INBOX.Trash',
    ingestEnabled: false,
    // File name of the sales/VAT report in payment-reports/: "<prefix>-September-2026.pdf".
    exportReportFilePrefix: 'Sales',
    // Name of an invoice PDF where it LEAVES the app — download, public link, mail attachment, the pull
    // by vitalpilze („D1317-26103102.pdf", Betreiber 31.10.2026, 48a). Placeholders: services/pdfName.ts.
    // The stored file keeps its own name (pdfFileName), and so does the monthly ZIP.
    invoicePdfNamePattern: '{brand}{ref}-{yy}{mm}{dd}{counter}',
    llmEnabled: false,
    llmModel: 'qwen2.5:7b',
    llmTimeoutMs: 60000,
    aboRunHour: 1,
    ingestPollHours: 6,
    paymentAutoImportEnabled: false,
    // Monthly payment auto-import (Betreiber, 18.10.2026): day 2 at 00:40 UTC — after the abo run
    // of the 1st, and late enough that PayPal (transactions up to 3 h late) has the month complete.
    paymentAutoImportDay: 2,
    paymentAutoImportTime: '00:40',
    logLevelDefault: 'info' as 'fatal' | 'error' | 'warn' | 'info' | 'debug',
    logRetentionDays: 30,
    vatOssMode: 'off' as 'off' | 'manual' | 'auto',
    vatOssThreshold: OSS_THRESHOLD_EUR,
    certSenderCountry: 'DE',
    certAddressOutput: 'label' as 'label' | 'endless' | 'envelope',
    certLabelPreset: 'zw3474',
    certLabelFillDirection: 'row' as 'row' | 'column',
    // Zweckform 3474 (70 × 37, 3 × 8, randlos) — the sizes add up to A4 exactly, which makes them
    // the most honest start value.
    certLabelWidthMm: 70,
    certLabelHeightMm: 37,
    certLabelColumns: 3,
    certLabelRows: 8,
    certLabelMarginTopMm: 0,
    certLabelMarginLeftMm: 0,
    certLabelGapXMm: 0,
    certLabelGapYMm: 0,
    certLabelStartPosition: 1,
    certEnvelopeFormat: 'C5' as 'C4' | 'C5' | 'DL',
    certNotifyMailEnabled: false,
    certNotifyTelegramEnabled: false,
}

// Defaults of the per-brand config (models/brandConfig.ts schema, services/brandConfig.ts fallback,
// the brand form in pages/Settings.tsx): payment provider, test mode of Stripe and Mollie, fee
// type of refund, author and per-article fees. · Status: offen (each entry)
export const BRAND_CONFIG_DEFAULTS = {
    paymentProvider: 'stripe' as 'stripe' | 'mollie',
    stripeTestmode: true,
    mollieTestmode: true,
    feeType: 'percent' as 'percent' | 'fixed',
}

// ── Betrieb ────────────────────────────────────────────────────────────────────────────────

// Listeners the domain event bus allows before Node warns. · Status: fest (Betreiber, 19.10.2026)
export const EVENT_BUS_MAX_LISTENERS = 50
// While shutting down, idle connections are closed at this interval. · Status: fest (Betreiber, 19.10.2026)
export const SHUTDOWN_IDLE_SWEEP_MS = 100

// ── Oberfläche (Client) ────────────────────────────────────────────────────────────────────

// How often the build/environment info is fetched. · Status: fest (Betreiber, 19.10.2026)
export const BUILD_INFO_POLL_MS = 60_000
// How long a success toast ('ok') stays; 'warn'/'err' stay until clicked away (Betreiber,
// 28.10.2026). · Status: fest (Betreiber, 19.10.2026)
export const TOAST_DURATION_MS = 5000
// The back-button guard is armed again after this. · Status: fest (Betreiber, 19.10.2026)
export const BACK_GUARD_REARM_MS = 150
// Debounces: customer search, list search, saving a column width, mail-frame preview, PDF preview
// of the template editor. · Status: fest (Betreiber, 19.10.2026)
export const CUSTOMER_SEARCH_DEBOUNCE_MS = 250
export const LIST_SEARCH_DEBOUNCE_MS = 250
export const COLUMN_WIDTH_SAVE_DELAY_MS = 400
export const MAIL_FRAME_PREVIEW_DEBOUNCE_MS = 400
export const TEMPLATE_RENDER_DEBOUNCE_MS = 600
// Table columns: start width and narrowest width (px). · Status: fest (Betreiber, 19.10.2026)
export const DEFAULT_COLUMN_WIDTH = 140
export const MIN_COLUMN_WIDTH = 60
// A tab title is cut after this many characters. · Status: fest (Betreiber, 19.10.2026)
export const MAX_TAB_TITLE_LENGTH = 30
// Receipt inbox: sender and subject are cut after this many characters. · Status: fest (Betreiber, 19.10.2026)
export const INGEST_FROM_MAX_CHARS = 28
export const INGEST_SUBJECT_MAX_CHARS = 32
// Template editor: position of the splitter (percent) at the start, and its limits. · Status: fest (Betreiber, 19.10.2026)
export const TEMPLATE_SPLIT_DEFAULT_PCT = 45
export const TEMPLATE_SPLIT_MIN_PCT = 25
export const TEMPLATE_SPLIT_MAX_PCT = 75
// Dashboard chart: „last N years“ looks back this many years before the current one, and the
// x-axis shows about this many labels at most. · Status: fest (Betreiber, 19.10.2026)
export const CHART_YEARS_BACK = 2
export const CHART_MAX_X_LABELS = 12

// Checklist marks (monthly export): the symbol per state and the class that colours it; the text
// per state is CHECK_MARKS in shared/texts. Only glyphs that take the column colour: ⚡ stays a
// yellow emoji on macOS whatever one asks for, so the source is ↯; \uFE0E keeps ✉ a text glyph.
// The order is the order of the legend. · Status: offen
export const CHECK_MARK_SYMBOLS = {
    auto: { sym: '↯', cls: 'check-mark-open' },
    autoDone: { sym: '↯✓', cls: 'check-mark-ok' },
    noMovement: { sym: '○✓', cls: 'check-mark-ok' },
    optional: { sym: '◌', cls: '' },
    mayBeEmpty: { sym: '∅', cls: '' },
    mailNote: { sym: '✉\uFE0E', cls: '' },
} as const

// ── Farben (in TypeScript; die Oberfläche selbst nutzt die CSS-Variablen in styles.css) ────

// Chart palette (brands / years), grid, axis text, zero line; dashboard income / expense / net.
// · Status: fest (Betreiber, 19.10.2026)
export const CHART_COLORS = ['#1c26c9', '#13b6a6', '#e8923a', '#9c5cc4', '#d6455d', '#5b8def']
export const CHART_GRID_COLOR = '#e6e8ee'
export const CHART_AXIS_TEXT_COLOR = '#8a8f9a'
export const CHART_ZERO_LINE_COLOR = '#b9bec9'
export const CHART_INCOME_COLOR = '#13b6a6'
export const CHART_EXPENSE_COLOR = '#d6455d'
export const CHART_NET_COLOR = '#1c26c9'
// Log viewer: badge colour per level, the fallback and the badge text. · Status: fest (Betreiber, 19.10.2026)
export const LOG_LEVEL_COLORS: Record<string, string> = {
    fatal: '#7c1d1d', error: '#dc2626', warn: '#d97706', info: '#2563eb', debug: '#6b7280',
}
export const LOG_LEVEL_FALLBACK_COLOR = '#6b7280'
export const LOG_BADGE_TEXT_COLOR = '#fff'
// VAT page: the OSS bar above the threshold. · Status: fest (Betreiber, 19.10.2026)
export const OSS_OVER_THRESHOLD_COLOR = '#c0392b'
// Mail-frame editor: background of the preview and of the logo. · Status: fest (Betreiber, 19.10.2026)
export const MAIL_PREVIEW_BACKGROUND = '#fff'
// Result page of an action link from a mail (receipt inbox): text and link colour. · Status: fest (Betreiber, 19.10.2026)
export const ACTION_PAGE_INK = '#1b212b'
export const ACTION_PAGE_LINK = '#1c26c9'

// Mail texts: the placeholder that becomes the link downloading THE document of the mail, without
// login (communication/backoffice-platzhalter-rechnungslink.md). Written in the texts as {<name>}.
// The name only stands here — server, hints and preview take it from this. · Status: fest (Betreiber, 27.10.2026, „97a“)
export const MAIL_PLACEHOLDER_DOCUMENT_URL = 'invoiceUrl'
// The placeholder that becomes a table „Beleg | Download" — every document of the mail with its own
// download link, also in a mail with several (operator, 27.10.2026, „142a", name „143b"). · Status: fest (Betreiber, 27.10.2026, „143b“)
export const MAIL_PLACEHOLDER_DOCUMENT_URL_LIST = 'invoiceUrlList'
// Every placeholder a mail text knows. Saving a text with another {name} in it is reported (operator,
// 27.10.2026, „144a"): a typo would otherwise reach the customer as literal braces. · Status: fest (Betreiber, 27.10.2026, „144a“)
export const MAIL_TEXT_PLACEHOLDERS = [
    'name', 'company', 'month', 'year', 'invoiceNumber', 'productlist',
    MAIL_PLACEHOLDER_DOCUMENT_URL, MAIL_PLACEHOLDER_DOCUMENT_URL_LIST,
] as const
// Random bytes of the key in that link (base64url → 43 characters): not guessable, no number in it. · Status: offen
export const DOCUMENT_LINK_TOKEN_BYTES = 32
// Mollie upgrade (104a): how long a claim on the abo (set just before the one-off payment is
// created) blocks a second upgrade when no payment id ever followed — a crash in between must not
// lock the member out for good. · Status: offen
export const UPGRADE_CLAIM_STALE_MS = 10 * 60 * 1000

// Mail frame per brand: logo file, logo width, footer bar and its text. Defaults of an editable
// frame (System → Vorlagen → E-Mail). · Status: fest (Betreiber, 19.10.2026)
export const MAIL_FRAME_DEFAULTS = {
    V: { logoFile: 'gfv.png', logoWidth: 240, footerColor: '#6b722f', footerTextColor: '#ffffff' },
    d: { logoFile: 'deja.png', logoWidth: 200, footerColor: '#1f2430', footerTextColor: '#ffffff' },
    C: { logoFile: 'converter.png', logoWidth: 120, footerColor: '#1f2430', footerTextColor: '#ffffff' },
}

// Code-drawn PDFs (sales report, fallback invoice, author statement, PSP statement, address
// labels): ink, rules, muted and label text, the note colour. Two strong rules on purpose: the
// report uses #999, the author statement #888. · Status: fest (Betreiber, 19.10.2026)
export const PDF_COLOR_INK = '#000'
export const PDF_COLOR_RULE = '#ccc'
export const PDF_COLOR_RULE_LIGHT = '#e5e5e5'
export const PDF_COLOR_RULE_STRONG = '#999'
export const STATEMENT_COLOR_RULE_STRONG = '#888'
export const PDF_COLOR_MUTED = '#555'
export const PDF_COLOR_LABEL = '#666'
export const PDF_COLOR_NOTE = '#8a6d3b'
// PSP statement: the line that shows a balance that does not add up. · Status: offen
export const PDF_COLOR_ALERT = '#b00020'
// PayPal monthly statement PDF: PayPal blue in the head, the secondary name line. · Status: fest (Betreiber, 19.10.2026)
export const PAYPAL_PDF_BRAND_COLOR = '#003087'
export const PAYPAL_PDF_SECONDARY_COLOR = '#444'
// Fallback certificate (no template): frame/title, accent, text, secondary text. · Status: fest (Betreiber, 19.10.2026)
export const CERT_COLORS = { primary: '#1f4e3d', accent: '#b8944d', ink: '#111111', muted: '#555555' }
// Colour of the template engine when a template names none. · Status: fest (Betreiber, 19.10.2026)
export const TEMPLATE_DEFAULT_COLOR = '#000'

// ── PDF: Schrift, Seite, Vorlagen-Engine ───────────────────────────────────────────────────

// Fonts of the code-drawn PDFs (PDFKit standard fonts). · Status: fest (Betreiber, 19.10.2026)
export const PDF_FONT_REGULAR = 'Helvetica'
export const PDF_FONT_BOLD = 'Helvetica-Bold'
export const PDF_FONT_ITALIC = 'Helvetica-Oblique'
// Page of the code-drawn PDFs: format, margin, left and right edge of the text (pt).
// · Status: fest (Betreiber, 19.10.2026)
export const PDF_PAGE_SIZE = 'A4'
export const PDF_MARGIN_PT = 50
export const PDF_CONTENT_LEFT_PT = 50
export const PDF_CONTENT_RIGHT_PT = 545
// Template engine: page and margins (mm) and font size until a template sets them, line width of
// a rule, most passes of placeholder replacement, longest template slug. · Status: fest (Betreiber, 19.10.2026)
export const TEMPLATE_DEFAULT_PAGE_MM = { w: 210, h: 297, mLeft: 20, mTop: 40, mRight: 15, mBottom: 40 }
export const TEMPLATE_DEFAULT_FONT_SIZE = 10
export const TEMPLATE_DEFAULT_LINE_WIDTH_MM = 0.2
export const TEMPLATE_MAX_PLACEHOLDER_PASSES = 20
export const TEMPLATE_SLUG_MAX_LENGTH = 60
// Digits of a certificate number (00042). · Status: fest (Betreiber, 19.10.2026)
export const CERTIFICATE_NUMBER_DIGITS = 5
// The template version every imported V1 invoice is stamped with — whatever version is active.
// Every V1 invoice was made with version-1: api.deja.ltd always took the template's own version,
// and there was only version-1 (communication/backoffice-vitalpilze-gfv-vorlagenversion.md).
// · Status: fest (Betreiber, 24.10.2026, „22b“)
export const V1_IMPORT_TEMPLATE_VERSION = 'version-1'

// ── Etiketten und Umschläge ────────────────────────────────────────────────────────────────

// Label sheets on offer (services/certificates/labelPresets.ts keeps the type and the checks); the
// name and the option text are LABEL_PRESET_NAMES / LABEL_PRESET_OPTION in shared/texts.
// · Status: fest (Betreiber, 19.10.2026)
export const LABEL_PRESET_DATA = [
    {
        // Randlos, geht auf A4 exakt auf: 3 × 70 = 210 mm, 8 × 37 = 296 mm (1 mm Rest unten).
        // Das ist die Geometrie, die bisher als Vorgabe im Code stand — sie war dort
        // faelschlich als 3475 beschriftet, siehe unten.
        key: 'zw3474',
        widthMm: 70, heightMm: 37, columns: 3, rows: 8,
        marginTopMm: 0, marginLeftMm: 0, gapXMm: 0, gapYMm: 0,
        fillDirection: 'row',
    },
    {
        // ⚠️ 3475 ist 70 × 36, NICHT 70 × 37 — und hat oben einen Rand. Von drei Haendlern
        // uebereinstimmend mit 70 × 36 und 24 Etiketten je Bogen angegeben; die Rechnung geht
        // auf (3 × 70 = 210, 4,43 + 8 × 36 = 292,43, Rest unten 4,57 mm — fast symmetrisch,
        // was fuer den Wert spricht). Der Wert 198,570 pt aus glabels ist 70,05 mm; das ist
        // Rundung in der Quelle und hier auf glatte 70 zurueckgeholt, sonst waere der Bogen
        // rechnerisch 0,15 mm zu breit.
        //
        // Der obere Rand von 4,43 mm stammt als EINZIGER Wert hier aus nur einer Quelle.
        key: 'zw3475',
        widthMm: 70, heightMm: 36, columns: 3, rows: 8,
        marginTopMm: 4.43, marginLeftMm: 0, gapXMm: 0, gapYMm: 0,
        fillDirection: 'row',
    },
    {
        // 2 × 105 = 210 exakt; 4,93 + 7 × 41 = 291,93, Rest unten 5,07 mm.
        key: 'zw3477',
        widthMm: 105, heightMm: 41, columns: 2, rows: 7,
        marginTopMm: 4.93, marginLeftMm: 0, gapXMm: 0, gapYMm: 0,
        fillDirection: 'row',
    },
] as const
// Envelope sizes in mm (DIN/ISO), landscape — an envelope is fed long edge first; the name and the
// option text are ENVELOPE_NAMES / ENVELOPE_OPTION in shared/texts. · Status: fest (Betreiber, 19.10.2026)
export const ENVELOPE_FORMATS: Record<string, { w: number, h: number }> = {
    C4: { w: 324, h: 229 },
    C5: { w: 229, h: 162 },
    DL: { w: 220, h: 110 },
}
// Luft zwischen der kleinen Absenderzeile und der Empfaengeranschrift, in Millimetern.
//
// Vorher stand hier nur der Zeilenvorschub der 6-pt-Schrift (8 pt), und der deckt ihre eigene
// Zeilenhoehe fast auf - echter Zwischenraum blieb etwa ein Punkt. Am Bildschirm sieht das
// nach Abstand aus, auf Papier klebt die Anschrift am Absender. Vom Betreiber am echten Bogen
// gemessen, 26.08.2026.
//
// Bewusst eine benannte Konstante und keine Zahl im Fluss: Wer das anfasst, soll sehen, dass
// es ein ABSTAND ist und keine Schriftmetrik, die man mitrechnen muss. · Status: fest (Betreiber, 19.10.2026)
export const SENDER_GAP_MM = 2
// Line box of the sender line itself (6-pt type), kept apart from the gap below it. · Status: fest (Betreiber, 19.10.2026)
export const SENDER_LINE_PT = 8
